"use client";

import React, { useState, useEffect } from "react";
import { useLiveTime } from "hooks/useLiveTime";
import { parsePromotionDateTime } from "Utilities/PromotionDateUtils";
import { notifications } from "@mantine/notifications";
import { Actions } from "Components/Admin/StyleComponent/Actions";
import { Card } from "Components/Admin/StyleComponent/Card";
import { Header } from "Components/Admin/StyleComponent/Header";
import { Table } from "Components/Admin/StyleComponent/Table";
import {
  useGetAllOverallPromotionsQuery,
  useCreateOverallPromotionMutation,
  useUpdateOverallPromotionMutation,
  useDeleteOverallPromotionMutation,
} from "Services/Admin/EcommerceDiscountApiSlice";
import {
  OverallPromotionDTO,
  OverallPromotionResponseDTO,
} from "Types/Admin/EcommerceDiscountType";

const PLANT = process.env.REACT_APP_PLANT ?? "";

const emptyForm: OverallPromotionDTO = {
  discountDesc: "",
  fromPrice: 0,
  isActive: "Y",
  isPriceSlab: "1",
  plant: PLANT,
  promotionType: "",
  promotionValue: 0,
  toPrice: 0,
  startDate: "",
  startTime: "",
  endDate: "",
  endTime: "",
};

const formatDateForInput = (dateStr?: string, timeStr?: string) => {
  if (!dateStr || !timeStr) return "";
  const [day, month, year] = dateStr.split("/");
  if (!day || !month || !year) return "";
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${timeStr}:00`;
};

const AdminOverall = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingOverallPromotion, setEditingOverallPromotion] =
    useState<OverallPromotionResponseDTO | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [selectAll, setSelectAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<OverallPromotionDTO>(emptyForm);

  const {
    data: overallPromotions,
    isLoading,
    isError,
  } = useGetAllOverallPromotionsQuery();

  const [createOverallPromotion, { isLoading: isCreating }] =
    useCreateOverallPromotionMutation();
  const [updateOverallPromotion, { isLoading: isUpdating }] =
    useUpdateOverallPromotionMutation();
  const [deleteOverallPromotion] = useDeleteOverallPromotionMutation();

  const now = useLiveTime();

  const end = parsePromotionDateTime(formData.endDate ?? "", formData.endTime ?? "");
  const isExpired = end ? now > end : false;

  useEffect(() => {
    if (isExpired && formData.isActive === "Y") {
      setFormData((prev) => ({ ...prev, isActive: "N" }));
    }
  }, [isExpired, formData.isActive]);

  const formLoading = isCreating || isUpdating || isSubmitting;
  const promotions: OverallPromotionResponseDTO[] = overallPromotions ?? [];

  const handleSelectAll = () => setSelectAll((prev) => !prev);

  const buildFormData = (
    promotion: OverallPromotionResponseDTO,
  ): OverallPromotionDTO => ({
    discountDesc: promotion.discountDesc,
    fromPrice: promotion.fromPrice,
    isActive: promotion.isActive,
    isPriceSlab: String(promotion.isPriceSlab),
    plant: PLANT,
    promotionType: promotion.promotionType,
    promotionValue: promotion.promotionValue,
    toPrice: promotion.toPrice,
    startDate: promotion.startDate ?? "",
    startTime: promotion.startTime ?? "",
    endDate: promotion.endDate ?? "",
    endTime: promotion.endTime ?? "",
  });

  const handleDateTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "start" | "end",
  ) => {
    const value = e.target.value;
    if (!value) return;
    const [datePart, timePart] = value.split("T");
    const [year, month, day] = datePart.split("-");
    const time = timePart.substring(0, 5);
    setFormData((prev) => ({
      ...prev,
      [`${type}Date`]: `${day}/${month}/${year}`,
      [`${type}Time`]: time,
    }));
  };

  const handleEdit = (promotion: OverallPromotionResponseDTO) => {
    setEditingOverallPromotion(promotion);
    setFormData(buildFormData(promotion));
    setIsViewMode(false);
    setShowForm(true);
  };

  const handleView = (promotion: OverallPromotionResponseDTO) => {
    setEditingOverallPromotion(promotion);
    setFormData(buildFormData(promotion));
    setIsViewMode(true);
    setShowForm(true);
  };

  const handleDelete = async (promotion: OverallPromotionResponseDTO) => {
    try {
      const response: any = await deleteOverallPromotion(promotion.id).unwrap();
      notifications.show({
        message: response?.message,
        color: "red",
      });
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formLoading) return;

    const from = Number(formData.fromPrice);
    const to = Number(formData.toPrice);

    if (to <= from) {
      notifications.show({
        message: "To Price must be greater than From Price.",
        color: "red",
      });
      return;
    }

    // Price range overlap check — only active/non-expired promos are considered.
    // Expired or inactive promos can share the same range; the admin can reuse it.
    const others = promotions.filter(
      (p) => !editingOverallPromotion || p.id !== editingOverallPromotion.id,
    );

    const isPromoLive = (p: OverallPromotionResponseDTO): boolean => {
      if (p.isActive !== "Y") return false;
      const promoEnd = parsePromotionDateTime(p.endDate ?? "", p.endTime ?? "");
      if (promoEnd && now > promoEnd) return false;
      return true;
    };

    // Block only if it overlaps a LIVE (active + not expired) promotion
    const overlapping = others.find((p) => {
      if (!isPromoLive(p)) return false;
      const pFrom = Number(p.fromPrice);
      const pTo = Number(p.toPrice);
      return from < pTo && to > pFrom;
    });

    if (overlapping) {
      notifications.show({
        message: `Price range ₹${from}–₹${to} overlaps with an active promotion "${overlapping.discountDesc}" (₹${overlapping.fromPrice}–₹${overlapping.toPrice}). Either change the price range or deactivate/expire that promotion first.`,
        color: "red",
        autoClose: 8000,
      });
      return;
    }


    setIsSubmitting(true);

    const payload = {
      ...formData,
      isPriceSlab: 1,
    };

    try {
      if (editingOverallPromotion && !isViewMode) {
        const response: any = await updateOverallPromotion({
          id: editingOverallPromotion.id,
          data: {
            ...editingOverallPromotion,
            ...payload,
          },
        }).unwrap();
        notifications.show({
          message: response?.message,
          color: "green",
        });
        setShowForm(false);
        setEditingOverallPromotion(null);
      } else if (!isViewMode) {
        const response: any = await createOverallPromotion(payload).unwrap();
        notifications.show({
          message: response?.message,
          color: "green",
        });
        setShowForm(false);
        setEditingOverallPromotion(null);
      }
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-red-500">
        Failed to load overall promotions.
      </div>
    );
  }

  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <Header title="Overall Promotion" />
            <button
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              onClick={() => {
                setFormData(emptyForm);
                setEditingOverallPromotion(null);
                setIsViewMode(false);
                setShowForm(true);
              }}
            >
              Add Overall Promotion
            </button>
          </div>

          <Card>
            <Actions hideEdit={false} />
            <Table
              headers={[
                "Description",
                "Promotion Type",
                "Promotion Value",
                "From Price",
                "To Price",
                "Status",
              ]}
              selectAll={selectAll}
              onSelectAll={handleSelectAll}
              totalItems={promotions.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              isLoading={isLoading}
            >
              {promotions.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="py-10 text-center text-sm text-gray-400"
                  >
                    No overall promotions found.
                  </td>
                </tr>
              ) : (
                promotions
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  )
                  .map((promotion) => (
                    <tr key={promotion.id} className="border-t">
                      <td className="py-3 px-4 text-sm">
                        {promotion.discountDesc}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {promotion.promotionType === "INR"
                          ? "Fixed Amount"
                          : promotion.promotionType === "%"
                            ? "Percentage"
                            : promotion.promotionType}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {promotion.promotionValue}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {promotion.fromPrice}
                      </td>
                      <td className="py-3 px-4 text-sm">{promotion.toPrice}</td>
                      <td className="py-3 px-4 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            promotion.isActive === "Y"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {promotion.isActive === "Y" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-3 px-4 flex gap-2">
                        <button
                          className="text-blue-500 hover:text-blue-700"
                          onClick={() => handleView(promotion)}
                          title="View"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path
                              fillRule="evenodd"
                              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        <button
                          className="text-blue-500 hover:text-blue-700"
                          onClick={() => handleEdit(promotion)}
                          title="Edit"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(promotion)}
                          title="Delete"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
              )}
            </Table>
          </Card>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingOverallPromotion(null);
            }}
          >
            ← Back
          </button>

          <Header
            title={
              isViewMode
                ? "View Overall Promotion"
                : editingOverallPromotion
                  ? "Edit Overall Promotion"
                  : "Add Overall Promotion"
            }
          />

          <form
            onSubmit={handleSubmit}
            className="font-body tracking-wider space-y-4"
          >
            <div className="bg-white px-6 py-12 rounded-md shadow-md my-6">
              {formData.isActive === "N" && (
                <div className="mb-6 flex items-start gap-3 rounded-md border border-yellow-300 bg-yellow-100 px-4 py-4 text-sm text-yellow-800">
                  <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-semibold">This promotion is currently inactive.</p>
                    <p className="mt-1 text-yellow-700">It may have expired or been manually disabled. To reactivate it, update the end date to a future date and time, then set the status back to <strong>Active</strong>.</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Promotion Description:*
                  </label>
                  <textarea
                    name="discountDesc"
                    value={formData.discountDesc}
                    onChange={handleChange}
                    readOnly={isViewMode}
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    maxLength={200}
                    placeholder="Enter promotion description (max 200 chars)"
                    required
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.discountDesc.length}/200 characters
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Promotion Type:*
                  </label>
                  <select
                    name="promotionType"
                    value={formData.promotionType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    disabled={isViewMode}
                    required
                  >
                    <option value="">Select Promotion Type</option>
                    <option value="%">Percentage</option>
                    <option value="INR">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Promotion Value:*
                  </label>
                  <input
                    type="number"
                    min="1"
                    name="promotionValue"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.promotionValue || ""}
                    onChange={handleChange}
                    onInput={(e) => {
                      const t = e.target as HTMLInputElement;
                      t.value = t.value.replace(/^0+(?=\d)/, "");
                    }}
                    readOnly={isViewMode}
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    From Price:*
                  </label>
                  <input
                    type="number"
                    min="1"
                    name="fromPrice"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.fromPrice || ""}
                    onChange={handleChange}
                    onInput={(e) => {
                      const t = e.target as HTMLInputElement;
                      t.value = t.value.replace(/^0+(?=\d)/, "");
                    }}
                    readOnly={isViewMode}
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    To Price:*
                  </label>
                  <input
                    type="number"
                    min={formData.fromPrice}
                    name="toPrice"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.toPrice || ""}
                    onChange={handleChange}
                    onInput={(e) => {
                      const t = e.target as HTMLInputElement;
                      t.value = t.value.replace(/^0+(?=\d)/, "");
                    }}
                    readOnly={isViewMode}
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Start Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={formatDateForInput(formData.startDate, formData.startTime)}
                    onChange={(e) => handleDateTimeChange(e, "start")}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    required
                    readOnly={isViewMode}
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    End Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={formatDateForInput(formData.endDate, formData.endTime)}
                    onChange={(e) => handleDateTimeChange(e, "end")}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    required
                    readOnly={isViewMode}
                    disabled={isViewMode}
                  />
                </div>

                <div className="pt-2 px-2">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Status:
                  </label>
                  <label className="px-2">
                    <input
                      type="radio"
                      name="isActive"
                      value="Y"
                      checked={formData.isActive === "Y"}
                      onChange={handleChange}
                      className="mr-1"
                      readOnly={isViewMode}
                      disabled={isViewMode}
                    />
                    Active
                  </label>
                  <label className="px-2">
                    <input
                      type="radio"
                      name="isActive"
                      value="N"
                      checked={formData.isActive === "N"}
                      onChange={handleChange}
                      className="mr-1"
                      readOnly={isViewMode}
                      disabled={isViewMode}
                    />
                    Inactive
                  </label>
                </div>
              </div>

              <hr className="border-gray-300 my-8" />

              <div className="flex items-center justify-start gap-3 mt-4">
                <button
                  className="px-6 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white text-blue-600 hover:bg-gray-50"
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingOverallPromotion(null);
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>

                {!isViewMode && (
                  <button
                    className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
                    type="submit"
                    disabled={formLoading}
                  >
                    {formLoading && (
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                    )}
                    {formLoading ? "Saving..." : "Save"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default AdminOverall;
